import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { useUnit } from 'effector-react';
import { $devConfig } from '../../state/appConfig';
import styles from './GlobalMap.module.css';
import { useI18n } from '../../i18n';

type GlobalMapControlsConfig = {
  minDistance?: number;
  maxDistance?: number;
  minX?: number;
  maxX?: number;
  minZ?: number;
  maxZ?: number;
  panSpeed?: number;
  zoomSpeed?: number;
  inertia?: number;
  initialDistance?: number;
};

interface GlobalMapProps {
  className?: string;
  onEnterScene?: () => void;
  cameraTiltDeg?: number;
  controlsConfig?: GlobalMapControlsConfig;
}

const MODEL_URL = new URL('../../assets/models/Hub_Light.glb', import.meta.url).href;
const DEFAULT_CAMERA_TILT_DEG = 45;
const DEFAULT_CONTROLS: Required<
  Pick<GlobalMapControlsConfig, 'minDistance' | 'maxDistance' | 'panSpeed' | 'zoomSpeed' | 'inertia'>
> = {
  minDistance: 512,
  maxDistance: 2048,
  panSpeed: 0.7,
  zoomSpeed: 0.002,
  inertia: 0.88,
};

const MAP_MODEL_ROTATION = Math.PI * 0.65; // Custom rotation for the existing model
const MAP_MODEL_SHIFT_X = 130; // Custom shift for the existing model
const MAP_MODEL_SHIFT_Z = 600; // Custom shift for the existing model

const MAP_MARKERS = [
  { id: 'kaaba', labelKey: 'kaaba', enabled: true, position: { x: 0.523, z: 0.594 } },
  { id: 'meeqat', labelKey: 'meeqat', enabled: false, position: { x: 0.502, z: 0.566 } },
  { id: 'mina', labelKey: 'mina', enabled: false, position: { x: 0.504, z: 0.517 } },
  { id: 'muzdalifah', labelKey: 'muzdalifah', enabled: false, position: { x: 0.521, z: 0.494 } },
  { id: 'arafat', labelKey: 'arafat', enabled: false, position: { x: 0.519, z: 0.422 } },
];
const GLOBAL_MAP_SIGHT_MARKER_RADIUS = 12;

export function GlobalMap({
  className,
  onEnterScene,
  cameraTiltDeg = DEFAULT_CAMERA_TILT_DEG,
  controlsConfig,
}: GlobalMapProps) {
  const i18n = useI18n();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const debugMarkersRef = useRef<THREE.Mesh[]>([]);
  const showSightMarkersRef = useRef(false);
  const { showSightMarkers } = useUnit($devConfig);

  useEffect(() => {
    showSightMarkersRef.current = showSightMarkers;
    debugMarkersRef.current.forEach((marker) => {
      marker.visible = showSightMarkers;
    });
  }, [showSightMarkers]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    container.innerHTML = '';

    const scene = new THREE.Scene();
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);

    const camera = new THREE.PerspectiveCamera(45, 1, 0.5, 5000);
    const tiltRadians = THREE.MathUtils.degToRad(
      THREE.MathUtils.clamp(cameraTiltDeg, 10, 89)
    );
    const focusPoint = new THREE.Vector3(0, 0, 0);
    const focusVelocity = new THREE.Vector3(0, 0, 0);
    const cameraTarget = new THREE.Vector3();
    const resolvedConfig: Required<GlobalMapControlsConfig> = {
      minDistance: controlsConfig?.minDistance ?? DEFAULT_CONTROLS.minDistance,
      maxDistance: controlsConfig?.maxDistance ?? DEFAULT_CONTROLS.maxDistance,
      minX: controlsConfig?.minX ?? -300,
      maxX: controlsConfig?.maxX ?? 300,
      minZ: controlsConfig?.minZ ?? -1250,
      maxZ: controlsConfig?.maxZ ?? 300,
      panSpeed: controlsConfig?.panSpeed ?? DEFAULT_CONTROLS.panSpeed,
      zoomSpeed: controlsConfig?.zoomSpeed ?? DEFAULT_CONTROLS.zoomSpeed,
      inertia: controlsConfig?.inertia ?? DEFAULT_CONTROLS.inertia,
      initialDistance: controlsConfig?.initialDistance ?? 1750,
    };
    const clampFocus = () => {
      const clampedX = THREE.MathUtils.clamp(focusPoint.x, resolvedConfig.minX, resolvedConfig.maxX);
      const clampedZ = THREE.MathUtils.clamp(focusPoint.z, resolvedConfig.minZ, resolvedConfig.maxZ);
      if (clampedX !== focusPoint.x) {
        focusPoint.x = clampedX;
        focusVelocity.x = 0;
      }
      if (clampedZ !== focusPoint.z) {
        focusPoint.z = clampedZ;
        focusVelocity.z = 0;
      }
    };

    const loader = new GLTFLoader();
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('/draco/');
    loader.setDRACOLoader(dracoLoader);
    let mapRoot: THREE.Object3D | null = null;
    const markerLayer = document.createElement('div');
    markerLayer.className = styles.markerLayer;
    markerLayer.dir = 'ltr';
    container.appendChild(markerLayer);
    const markerVector = new THREE.Vector3();
    const markerCenteringTransform = ' translate(-50%, -50%)';
    const mapMarkers: Array<{
      element: HTMLButtonElement;
      anchor: THREE.Object3D;
    }> = [];

    const markerAnchorGroup = new THREE.Group();
    markerAnchorGroup.visible = showSightMarkers;
    scene.add(markerAnchorGroup);
    const markerAnchorGeometry = new THREE.SphereGeometry(
      GLOBAL_MAP_SIGHT_MARKER_RADIUS,
      12,
      12
    );
    const markerAnchorMaterial = new THREE.MeshBasicMaterial({
      color: 0xff00ff,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      depthTest: false,
    });

    loader.load(
      MODEL_URL,
      (gltf) => {
        mapRoot = gltf.scene;
        gltf.scene.rotation.y = MAP_MODEL_ROTATION;
        scene.add(gltf.scene);

        const bounds = new THREE.Box3().setFromObject(gltf.scene);
        const mapCenter = bounds.getCenter(new THREE.Vector3());
        gltf.scene.position.set(-mapCenter.x - MAP_MODEL_SHIFT_X, -mapCenter.y, -mapCenter.z - MAP_MODEL_SHIFT_Z);

        const adjustedBounds = new THREE.Box3().setFromObject(gltf.scene);
        const resolveMarkerCoord = (value: number, min: number, max: number) => {
          if (value >= 0 && value <= 1) {
            return THREE.MathUtils.lerp(min, max, value);
          }
          return value;
        };

        MAP_MARKERS.forEach((marker) => {
          const labelText =
            i18n.globalMap.markerLabels[marker.labelKey] ?? marker.labelKey;
          const element = document.createElement('button');
          element.type = 'button';
          element.className = `scene-poi-marker ${styles.mapMarker}`;
          element.setAttribute('aria-label', labelText);
          element.classList.add('is-hidden');
          element.disabled = !marker.enabled;

          const dot = document.createElement('span');
          dot.className = 'scene-poi-marker__dot';
          dot.setAttribute('aria-hidden', 'true');
          const label = document.createElement('span');
          label.className = 'scene-poi-marker__label';
          label.dir = document.documentElement.dir;
          label.textContent = labelText;

          element.append(dot, label);
          markerLayer.appendChild(element);

          const position = new THREE.Vector3(
            resolveMarkerCoord(
              marker.position.x,
              adjustedBounds.min.x,
              adjustedBounds.max.x
            ),
            0,
            resolveMarkerCoord(
              marker.position.z,
              adjustedBounds.min.z,
              adjustedBounds.max.z
            )
          );
          const anchor = new THREE.Mesh(
            markerAnchorGeometry,
            markerAnchorMaterial
          );
          anchor.position.copy(position);
          anchor.visible = showSightMarkersRef.current;
          anchor.renderOrder = 10;
          markerAnchorGroup.add(anchor);

          if (marker.enabled && onEnterScene) {
            element.addEventListener('click', () => onEnterScene());
          }

          mapMarkers.push({ element, anchor });
          debugMarkersRef.current.push(anchor);
        });

        distance = THREE.MathUtils.clamp(
          distance,
          resolvedConfig.minDistance,
          resolvedConfig.maxDistance
        );
        clampFocus();
        syncCamera();
      },
      undefined,
      (error) => {
        console.error('Failed to load map model', error);
      }
    );

    const resize = () => {
      const width = Math.max(container.clientWidth, 1);
      const height = Math.max(container.clientHeight, 1);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(width, height);
    };

    resize();

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);

    const raycaster = new THREE.Raycaster();
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const pointerMap = new Map<number, { x: number; y: number }>();
    let lastPointerPosition: { x: number; y: number } | null = null;
    let lastPinchDistance = 0;
    let lastPinchPoint: { x: number; y: number } | null = null;
    let isDragging = false;
    let distance = THREE.MathUtils.clamp(
      resolvedConfig.initialDistance,
      resolvedConfig.minDistance,
      resolvedConfig.maxDistance
    );
    renderer.domElement.style.touchAction = 'none';

    const syncCamera = () => {
      clampFocus();
      const height = Math.sin(tiltRadians) * distance;
      const depth = Math.cos(tiltRadians) * distance;
      camera.position.set(focusPoint.x, height, focusPoint.z + depth);
      cameraTarget.set(focusPoint.x, 0, focusPoint.z);
      camera.lookAt(cameraTarget);
    };

    const resolvePlanePoint = (clientX: number, clientY: number, target: THREE.Vector3) => {
      const rect = renderer.domElement.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);
      return raycaster.ray.intersectPlane(plane, target);
    };

    const applyPanDelta = (delta: THREE.Vector3) => {
      if (!Number.isFinite(delta.x) || !Number.isFinite(delta.z)) {
        return;
      }
      delta.multiplyScalar(resolvedConfig.panSpeed);
      focusPoint.add(delta);
      focusVelocity.copy(delta);
      clampFocus();
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (event.button !== 0 && event.pointerType !== 'touch') {
        return;
      }
      syncCamera();
      renderer.domElement.setPointerCapture(event.pointerId);
      pointerMap.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointerMap.size === 1) {
        lastPointerPosition = { x: event.clientX, y: event.clientY };
        lastPinchDistance = 0;
        lastPinchPoint = null;
      } else if (pointerMap.size === 2) {
        const pointers = Array.from(pointerMap.values());
        lastPinchDistance = Math.hypot(
          pointers[0].x - pointers[1].x,
          pointers[0].y - pointers[1].y
        );
        lastPinchPoint = {
          x: (pointers[0].x + pointers[1].x) * 0.5,
          y: (pointers[0].y + pointers[1].y) * 0.5,
        };
        lastPointerPosition = null;
      }
      isDragging = true;
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (!pointerMap.has(event.pointerId)) {
        return;
      }
      syncCamera();
      pointerMap.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointerMap.size === 1) {
        if (!lastPointerPosition) {
          return;
        }
        const prevPoint = resolvePlanePoint(
          lastPointerPosition.x,
          lastPointerPosition.y,
          new THREE.Vector3()
        );
        const nextPoint = resolvePlanePoint(event.clientX, event.clientY, new THREE.Vector3());
        if (!prevPoint || !nextPoint) {
          return;
        }
        const panDelta = prevPoint.sub(nextPoint);
        applyPanDelta(panDelta);
        lastPointerPosition = { x: event.clientX, y: event.clientY };
      } else if (pointerMap.size >= 2) {
        const pointers = Array.from(pointerMap.values());
        const mid = {
          x: (pointers[0].x + pointers[1].x) * 0.5,
          y: (pointers[0].y + pointers[1].y) * 0.5,
        };
        const pinchDistance = Math.hypot(
          pointers[0].x - pointers[1].x,
          pointers[0].y - pointers[1].y
        );
        if (lastPinchDistance > 0) {
          const ratio = lastPinchDistance / Math.max(1, pinchDistance);
          distance = THREE.MathUtils.clamp(
            distance * ratio,
            resolvedConfig.minDistance,
            resolvedConfig.maxDistance
          );
        }
        if (lastPinchPoint) {
          syncCamera();
          const prevPoint = resolvePlanePoint(
            lastPinchPoint.x,
            lastPinchPoint.y,
            new THREE.Vector3()
          );
          const nextPoint = resolvePlanePoint(mid.x, mid.y, new THREE.Vector3());
          if (prevPoint && nextPoint) {
            const panDelta = prevPoint.sub(nextPoint);
            applyPanDelta(panDelta);
          }
        }
        lastPinchDistance = pinchDistance;
        lastPinchPoint = mid;
      }
    };

    const handlePointerUp = (event: PointerEvent) => {
      pointerMap.delete(event.pointerId);
      if (pointerMap.size === 0) {
        lastPointerPosition = null;
        lastPinchDistance = 0;
        lastPinchPoint = null;
        isDragging = false;
      }
    };

    const handlePointerLeave = () => {
      pointerMap.clear();
      lastPointerPosition = null;
      lastPinchDistance = 0;
      lastPinchPoint = null;
      isDragging = false;
    };

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const zoomFactor = Math.exp(event.deltaY * resolvedConfig.zoomSpeed);
      distance = THREE.MathUtils.clamp(
        distance * zoomFactor,
        resolvedConfig.minDistance,
        resolvedConfig.maxDistance
      );
      syncCamera();
    };

    renderer.domElement.addEventListener('pointerdown', handlePointerDown);
    renderer.domElement.addEventListener('pointermove', handlePointerMove);
    renderer.domElement.addEventListener('pointerup', handlePointerUp);
    renderer.domElement.addEventListener('pointercancel', handlePointerUp);
    renderer.domElement.addEventListener('pointerleave', handlePointerLeave);
    window.addEventListener('blur', handlePointerLeave);
    renderer.domElement.addEventListener('wheel', handleWheel, { passive: false });

    let frameId = 0;
    const animate = () => {
      frameId = window.requestAnimationFrame(animate);
      if (!isDragging && (Math.abs(focusVelocity.x) > 0.0001 || Math.abs(focusVelocity.z) > 0.0001)) {
        focusPoint.add(focusVelocity);
        focusVelocity.multiplyScalar(resolvedConfig.inertia);
        clampFocus();
      } else if (!isDragging) {
        focusVelocity.set(0, 0, 0);
      }
      syncCamera();
      if (mapMarkers.length > 0) {
        const width = Math.max(container.clientWidth, 1);
        const height = Math.max(container.clientHeight, 1);
        for (const marker of mapMarkers) {
          markerVector.copy(marker.anchor.position).project(camera);
          const isHidden =
            !Number.isFinite(markerVector.x) ||
            !Number.isFinite(markerVector.y) ||
            markerVector.z < -1 ||
            markerVector.z > 1 ||
            Math.abs(markerVector.x) > 1.2 ||
            Math.abs(markerVector.y) > 1.2;
          marker.element.classList.toggle('is-hidden', isHidden);
          if (!isHidden) {
            const screenX = (markerVector.x * 0.5 + 0.5) * width;
            const screenY = (-markerVector.y * 0.5 + 0.5) * height;
            marker.element.style.transform = `translate3d(${screenX}px, ${screenY}px, 0)${markerCenteringTransform}`;
          }
        }
      }
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      resizeObserver.disconnect();
      window.cancelAnimationFrame(frameId);
      renderer.domElement.removeEventListener('pointerdown', handlePointerDown);
      renderer.domElement.removeEventListener('pointermove', handlePointerMove);
      renderer.domElement.removeEventListener('pointerup', handlePointerUp);
      renderer.domElement.removeEventListener('pointercancel', handlePointerUp);
      renderer.domElement.removeEventListener('pointerleave', handlePointerLeave);
      window.removeEventListener('blur', handlePointerLeave);
      renderer.domElement.removeEventListener('wheel', handleWheel);
      dracoLoader.dispose();
      renderer.dispose();
      debugMarkersRef.current = [];
      markerAnchorGeometry.dispose();
      markerAnchorMaterial.dispose();
      scene.remove(markerAnchorGroup);
      markerLayer.remove();
      mapMarkers.splice(0);
      if (mapRoot) {
        scene.remove(mapRoot);
      }
      container.innerHTML = '';
    };
  }, [cameraTiltDeg, controlsConfig, i18n, onEnterScene]);

  const rootClassName = className ? `${styles.root} ${className}` : styles.root;

  return <div ref={containerRef} className={rootClassName} />;
}
